// „Handlowcy” (decision 2026-10-29-handlowcy-marza-oplacona): per Opiekun of
// a ZK, the invoiced margin of the period, its part on ZK marked „Opłacone”
// and the rest, and the ZK behind it. Prowizji nie liczymy — Emil ustala je
// indywidualnie; this is only the base. Pure folding of the ADMIN-only
// `forecastVsActual` rows the page already loaded: the same rows as the
// Rachunek wyników lines (profit at sale), so „Razem” = the period's actual
// revenue and margin there. Nothing here recomputes money or eligibility.
import type { ForecastVsActualOrder, IncomeStatementResponse } from '../incomeStatement/incomeStatementApi';
import { foldOrders, isListedOrder, type BreakdownOrderRow } from '../managementDashboard/breakdownModel';
import { exportSheetName, type PivotExportTable } from '../incomeStatement/pivotExport';

/**
 * PAID = the manual ZK flag „Opłacone”. FACTORING = a ZK paid by factoring:
 * the factor pays at once (decision 2026-10-27, as in Płynność), so it counts
 * as paid from its invoice — an assumption to confirm (pytania do biznesu,
 * „Handlowcy”). UNPAID = everything else.
 */
export type SalesRepPayment = 'PAID' | 'FACTORING' | 'UNPAID';

export interface SalesRepOrder extends BreakdownOrderRow {
  payment: SalesRepPayment;
}

export interface SalesRepMoney {
  /** Distinct ZK invoiced in the period. */
  orderCount: number;
  revenue: number;
  /** The known part when `costPending`; a fully unknown cost counts 0 (decision 2026-10-29 pt 8). */
  margin: number;
  /** revenue − margin. */
  cost: number;
  paidMargin: number;
  paidCount: number;
  unpaidMargin: number;
  unpaidCount: number;
  /** Some ZK has only the known part of its cost (after the period is summed). */
  costPending: boolean;
  paidPending: boolean;
  unpaidPending: boolean;
  /** Margin of the period's forecast still waiting for an invoice (plan). */
  waitingMargin: number;
  waitingCount: number;
  waitingPending: boolean;
}

export interface SalesRepRow extends SalesRepMoney {
  /** Opiekun id (name when the id is missing), `none` = „Bez opiekuna”. */
  key: string;
  /** null = „Bez opiekuna”. */
  name: string | null;
  /** Invoiced ZK of the period, largest margin first (a ZK netting to ~0 is not listed). */
  orders: SalesRepOrder[];
}

export interface SalesRepsResult {
  reps: SalesRepRow[];
  total: SalesRepMoney;
  /** Reps with an invoiced ZK, „Bez opiekuna” not counted. */
  repCount: number;
}

export const NO_REP_KEY = 'none';

function repOf(row: ForecastVsActualOrder): { key: string; name: string | null } {
  const manager = row.accountManager;
  if (!manager) return { key: NO_REP_KEY, name: null };
  return { key: manager.id || `name:${manager.name}`, name: manager.name };
}

export function paymentOf(row: Pick<ForecastVsActualOrder, 'customerPaymentStatus' | 'paymentMode'>): SalesRepPayment {
  if (row.customerPaymentStatus === 'PAID') return 'PAID';
  if (row.paymentMode === 'FACTORING') return 'FACTORING';
  return 'UNPAID';
}

const isPaid = (payment: SalesRepPayment) => payment !== 'UNPAID';
// Unknown cost: its known part is 0, so the margin is the revenue (pt 8).
const marginOf = (row: { margin: number | null; revenue: number }) => row.margin ?? row.revenue;

function emptyMoney(): SalesRepMoney {
  return {
    orderCount: 0, revenue: 0, margin: 0, cost: 0,
    paidMargin: 0, paidCount: 0, unpaidMargin: 0, unpaidCount: 0,
    costPending: false, paidPending: false, unpaidPending: false,
    waitingMargin: 0, waitingCount: 0, waitingPending: false,
  };
}

// Money from every row (so „Razem” = Rachunek wyników to the grosz); a ZK
// netting to ~0 (`isListedOrder`) is neither counted nor listed.
function addInvoiced(target: SalesRepMoney, order: SalesRepOrder): void {
  const margin = marginOf(order);
  const counted = isListedOrder(order) ? 1 : 0;
  target.orderCount += counted;
  target.revenue += order.revenue;
  target.margin += margin;
  target.cost = target.revenue - target.margin;
  target.costPending = target.costPending || order.costPending;
  if (isPaid(order.payment)) {
    target.paidMargin += margin;
    target.paidCount += counted;
    target.paidPending = target.paidPending || order.costPending;
  } else {
    target.unpaidMargin += margin;
    target.unpaidCount += counted;
    target.unpaidPending = target.unpaidPending || order.costPending;
  }
}

function addWaiting(target: SalesRepMoney, order: BreakdownOrderRow): void {
  target.waitingMargin += marginOf(order);
  target.waitingCount += isListedOrder(order) ? 1 : 0;
  target.waitingPending = target.waitingPending || order.costPending;
}

/**
 * The reps of the period's months (`YYYY-MM`). null = the block is not
 * available to this reader (ADMIN only) — never an empty list.
 */
export function buildSalesReps(statement: IncomeStatementResponse, months: Iterable<string>): SalesRepsResult | null {
  const block = statement.forecastVsActual;
  if (!block) return null;
  const monthSet = new Set(months);
  const inPeriod = (rows: ForecastVsActualOrder[]) => rows.filter((row) => row.month && monthSet.has(row.month));
  const actualRows = inPeriod(block.orders.actual);
  const forecastRows = inPeriod(block.orders.forecast);
  // Identity of a ZK (Opiekun, payment) — the same on every row of that ZK.
  const identity = new Map<string, ForecastVsActualOrder>();
  [...actualRows, ...forecastRows].forEach((row) => { if (!identity.has(row.id)) identity.set(row.id, row); });

  const reps = new Map<string, SalesRepRow>();
  const repFor = (orderId: string) => {
    const { key, name } = repOf(identity.get(orderId)!);
    const current = reps.get(key) || { ...emptyMoney(), key, name, orders: [] };
    reps.set(key, current);
    return current;
  };
  const total = emptyMoney();

  // One row per ZK, its „*” after the period's months are summed (`pendingScope.ts`).
  for (const folded of foldOrders(actualRows, 'actual')) {
    const order: SalesRepOrder = { ...folded, payment: paymentOf(identity.get(folded.id)!) };
    const rep = repFor(order.id);
    if (isListedOrder(order)) rep.orders.push(order);
    addInvoiced(rep, order);
    addInvoiced(total, order);
  }
  for (const folded of foldOrders(forecastRows, 'forecast')) {
    addWaiting(repFor(folded.id), folded);
    addWaiting(total, folded);
  }

  const list = Array.from(reps.values())
    .map((rep) => ({ ...rep, orders: [...rep.orders].sort((a, b) => marginOf(b) - marginOf(a)) }))
    // Largest invoiced margin first; „Bez opiekuna” always last.
    .sort((a, b) => {
      if ((a.key === NO_REP_KEY) !== (b.key === NO_REP_KEY)) return a.key === NO_REP_KEY ? 1 : -1;
      return b.margin - a.margin || b.waitingMargin - a.waitingMargin || (a.name || '').localeCompare(b.name || '', 'pl');
    });
  return {
    reps: list,
    total,
    repCount: list.filter((rep) => rep.key !== NO_REP_KEY && rep.orderCount > 0).length,
  };
}

/** Margin % of the amounts shown (decision 2026-10-29 pt 8); null without revenue. */
export function repMarginPercent(money: Pick<SalesRepMoney, 'margin' | 'revenue'>): number | null {
  return money.revenue > 0 ? (money.margin / money.revenue) * 100 : null;
}

// ─── Excel (POST /reports/sales-pivot/export, the „Skąd ta liczba” mechanism) ──

const whole = (value: number) => Math.round(value);
const onePlace = (value: number | null) => (value == null ? null : Math.round(value * 10) / 10);

export interface SalesRepsExportLabels {
  title: string;
  sheet: string;
  info: string[];
  rep: string;
  orders: string;
  revenue: string;
  margin: string;
  marginPercent: string;
  paid: string;
  unpaid: string;
  waiting: string;
  total: string;
  repName: (rep: SalesRepRow) => string;
}

export function buildSalesRepsExport(result: SalesRepsResult, labels: SalesRepsExportLabels): PivotExportTable {
  // „*” in the file next to an amount that holds an incomplete cost, like the screen.
  const marks = (money: SalesRepMoney) => {
    const marked = [
      ...(money.costPending ? [3, 4] : []),
      ...(money.paidPending ? [5] : []),
      ...(money.unpaidPending ? [6] : []),
      ...(money.waitingPending ? [7] : []),
    ];
    return marked.length ? { marked } : {};
  };
  const cells = (label: string, money: SalesRepMoney) => [
    label, money.orderCount, whole(money.revenue), whole(money.margin), onePlace(repMarginPercent(money)),
    whole(money.paidMargin), whole(money.unpaidMargin), whole(money.waitingMargin),
  ];
  return {
    title: labels.title,
    sheetName: exportSheetName(labels.sheet),
    info: labels.info,
    columns: [
      { header: labels.rep, format: 'text' },
      { header: labels.orders, format: 'count' },
      { header: labels.revenue, format: 'money' },
      { header: labels.margin, format: 'money' },
      { header: labels.marginPercent, format: 'percent' },
      { header: labels.paid, format: 'money' },
      { header: labels.unpaid, format: 'money' },
      { header: labels.waiting, format: 'money' },
    ],
    rows: [
      ...result.reps.map((rep) => ({ kind: 'order' as const, level: 0, cells: cells(labels.repName(rep), rep), ...marks(rep) })),
      { kind: 'total' as const, level: 0, cells: cells(labels.total, result.total), ...marks(result.total) },
    ],
  };
}

export interface SalesRepOrdersExportLabels {
  title: string;
  sheet: string;
  info: string[];
  order: string;
  customer: string;
  months: string;
  revenue: string;
  cost: string;
  margin: string;
  marginPercent: string;
  payment: string;
  total: string;
  orderLabel: (number: string) => string;
  monthsLabel: (months: string[]) => string;
  paymentLabel: (payment: SalesRepPayment) => string;
}

export function buildSalesRepOrdersExport(rep: SalesRepRow, labels: SalesRepOrdersExportLabels): PivotExportTable {
  const marked = (pending: boolean) => (pending ? { marked: [4, 5, 6] } : {});
  return {
    title: labels.title,
    sheetName: exportSheetName(labels.sheet),
    info: labels.info,
    columns: [
      { header: labels.order, format: 'text' },
      { header: labels.customer, format: 'text' },
      { header: labels.months, format: 'text' },
      { header: labels.revenue, format: 'money' },
      { header: labels.cost, format: 'money' },
      { header: labels.margin, format: 'money' },
      { header: labels.marginPercent, format: 'percent' },
      { header: labels.payment, format: 'text' },
    ],
    rows: [
      ...rep.orders.map((order) => {
        const margin = marginOf(order);
        return {
          kind: 'order' as const,
          level: 0,
          cells: [
            labels.orderLabel(order.displayOrderNumber), order.customerName, labels.monthsLabel(order.months),
            whole(order.revenue), whole(order.revenue - margin), whole(margin),
            onePlace(repMarginPercent({ margin, revenue: order.revenue })), labels.paymentLabel(order.payment),
          ],
          ...marked(order.costPending),
        };
      }),
      {
        kind: 'total' as const,
        level: 0,
        cells: [labels.total, '', '', whole(rep.revenue), whole(rep.cost), whole(rep.margin), onePlace(repMarginPercent(rep)), ''],
        ...marked(rep.costPending),
      },
    ],
  };
}

export const salesRepOrderMargin = marginOf;
